/**
 * FlightCalendar.jsx - 機体予約・フライト管理 (Neo-Futuristic)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import jaLocale from '@fullcalendar/core/locales/ja';
import { flightApi, droneApi, userApi } from '../services/droneOpsApi';
import { naturalSortBy } from '../services/utils';

const STATUS_LABELS = {
  planned:   '予約中',
  in_flight: '飛行中',
  completed: '完了',
  cancelled: 'キャンセル',
};

const STATUS_COLORS = {
  in_flight: '#10B981',
  completed: '#6B7280',
  cancelled: '#EF4444',
};

const FLIGHT_CATEGORIES = {
  flight:     { label: '飛行',   color: '#0066FF' },
  inspection: { label: '点検',   color: '#FF6600' },
  training:   { label: '訓練',   color: '#9900FF' },
  demo:       { label: 'デモ',   color: '#00CCFF' },
  survey:     { label: '測量',   color: '#00AA00' },
  spray:      { label: '散布',   color: '#CCCC00' },
  other:      { label: 'その他', color: '#666666' },
};

const EMPTY_FORM = {
  thingName: '',
  groupName: '',
  pilotId:   '',
  pilotName: '',
  category:  'flight',
  title:     '',
  startTime: '',
  endTime:   '',
  location:  '',
  purpose:   '',
  notes:     '',
  altitude:  '',
};

// ---------- ローカル Hud* コンポーネント ----------

function MSIcon({ name, className = 'text-xl' }) {
  return <span className={`material-symbols-outlined leading-none select-none ${className}`}>{name}</span>;
}

function HudSpinner({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 border-2 border-primary/20" />
        <div className="absolute inset-0 border-t-2 border-primary animate-spin" />
        <div className="absolute inset-2 border border-primary/10" />
      </div>
      {label && <span className="font-label-caps text-sm text-primary/60 tracking-[0.2em]">{label}</span>}
    </div>
  );
}

function HudButton({ children, icon, iconClassName, variant = 'primary', size = 'md', loading, onClick, disabled, type = 'button' }) {
  const base = 'rounded-none font-label-caps tracking-[0.15em] flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed';
  const sizes = {
    sm:   'h-8 px-3 text-[11px]',
    md:   'h-10 px-4 text-[13px]',
    wide: 'h-10 px-8 text-[13px]',
    lg:   'h-14 px-8 text-base font-extrabold',
  };
  const variants = {
    primary:   'bg-primary text-on-primary hover:shadow-[0_0_20px_rgba(162,201,255,0.6)]',
    secondary: 'bg-transparent border border-primary/50 text-primary hover:border-primary hover:bg-primary/10',
    danger:    'bg-transparent border border-error/50 text-error hover:border-error hover:bg-error/10',
  };
  const defaultIconClass = size === 'lg' ? 'text-2xl' : 'text-base';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${base} ${sizes[size] ?? sizes.md} ${variants[variant] ?? variants.primary}`}
    >
      {loading ? (
        <>
          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          PROCESSING...
        </>
      ) : (
        <>
          {icon && <MSIcon name={icon} className={iconClassName ?? defaultIconClass} />}
          {children}
        </>
      )}
    </button>
  );
}

function HudAlert({ message, variant = 'error' }) {
  if (!message) return null;
  const styles = {
    error:   'border-error/50 bg-error-container/30 text-error',
    warning: 'border-tertiary/50 bg-tertiary-container/20 text-tertiary',
    success: 'border-secondary/50 bg-secondary-container/20 text-secondary',
  };
  const icons = { error: 'warning', warning: 'info', success: 'check_circle' };
  return (
    <div className={`border ${styles[variant] ?? styles.error} px-4 py-3 flex items-center gap-3`}>
      <MSIcon name={icons[variant] ?? 'warning'} className="text-base flex-shrink-0" />
      <span className="font-label-caps text-[13px] tracking-wider">{message}</span>
    </div>
  );
}

function HudModal({ isOpen, onClose, title, children, footer, size = 'md' }) {
  const [mounted,   setMounted]   = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setIsClosing(false);
    } else if (mounted) {
      setIsClosing(true);
    }
  }, [isOpen]); // eslint-disable-line

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  const sizeClass = size === 'lg' ? 'max-w-2xl' : size === 'sm' ? 'max-w-sm' : 'max-w-lg';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-4">
      <div
        className={`absolute inset-0 bg-slate-950/50 backdrop-blur-sm ${isClosing ? 'hud-backdrop-fold' : 'hud-backdrop-rise'}`}
        onClick={onClose}
      />
      <div
        className={`${isClosing ? 'hud-fold' : 'hud-rise'} relative w-full ${sizeClass} glass-panel shadow-[0_-20px_50px_rgba(0,104,183,0.2)] flex flex-col max-h-[90vh]`}
        onAnimationEnd={() => { if (isClosing) setMounted(false); }}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-blue-900/40 flex-shrink-0">
          <span className="font-label-caps text-[15px] text-blue-400 tracking-[0.2em] uppercase">{title}</span>
          <button onClick={onClose} className="p-1.5 border border-blue-900/50 hover:bg-blue-900/30 transition-colors">
            <MSIcon name="close" className="text-base text-slate-400" />
          </button>
        </div>
        <div className="overflow-auto p-5 flex-1">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-blue-900/40 flex-shrink-0">
            {footer}
          </div>
        )}
        <div className="flex justify-center py-2 border-t border-blue-900/20 flex-shrink-0">
          <div className="w-10 h-0.5 bg-blue-900/50 rounded-full" />
        </div>
      </div>
    </div>
  );
}

function HudInput({ label, name, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div>
      {label && (
        <label className="block font-label-caps text-xs tracking-[0.15em] text-slate-500 mb-1 uppercase">
          {label}
        </label>
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-blue-900/50 text-on-surface text-sm py-1.5 px-0 focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
      />
    </div>
  );
}

function HudSelect({ label, name, value, onChange, options = [] }) {
  return (
    <div>
      {label && (
        <label className="block font-label-caps text-xs tracking-[0.15em] text-slate-500 mb-1 uppercase">
          {label}
        </label>
      )}
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full bg-surface-container-high border-b border-blue-900/50 text-on-surface text-sm py-1.5 px-1 focus:outline-none focus:border-blue-500"
      >
        {options.map((opt, i) => (
          <option key={i} value={opt.value} disabled={opt.disabled} className="bg-surface-container text-on-surface">
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function HudTextarea({ label, name, value, onChange, rows = 3, placeholder = '' }) {
  return (
    <div>
      {label && (
        <label className="block font-label-caps text-xs tracking-[0.15em] text-slate-500 mb-1 uppercase">
          {label}
        </label>
      )}
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        className="w-full bg-surface-container-high border border-blue-900/30 text-on-surface text-sm p-2 focus:outline-none focus:border-blue-500/50 placeholder:text-slate-600 resize-none"
      />
    </div>
  );
}

function HudBadge({ children, variant = 'default' }) {
  const styles = {
    default: 'border-outline/40 text-outline',
    primary: 'border-primary/40 text-primary',
    success: 'border-secondary/40 text-secondary',
    danger:  'border-error/40 text-error',
    warning: 'border-tertiary/40 text-tertiary',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border font-label-caps text-[10px] rounded-full ${styles[variant] ?? styles.default}`}>
      {children}
    </span>
  );
}

// ---------- メインコンポーネント ----------

export default function FlightCalendar() {
  const calendarRef = useRef(null);

  const [flights,  setFlights]  = useState([]);
  const [drones,   setDrones]   = useState([]);
  const [groups,   setGroups]   = useState([]);
  const [users,    setUsers]    = useState([]);

  const [loading,        setLoading]        = useState(true);
  const [showModal,      setShowModal]      = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [formError,      setFormError]      = useState('');
  const [submitting,     setSubmitting]     = useState(false);
  const [currentRange,   setCurrentRange]   = useState(null);
  const [groupConfirm,   setGroupConfirm]   = useState(null);

  const fetchFlights = useCallback(async (from, to) => {
    try {
      const data = await flightApi.list({ from, to });
      setFlights(data.flights ?? []);
    } catch (e) {
      console.error('予約取得エラー:', e);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [droneData, userData] = await Promise.all([
          droneApi.list(),
          userApi.list(),
        ]);
        const droneList = droneData.drones ?? [];
        setDrones(naturalSortBy(droneList, 'thingName'));
        setUsers(naturalSortBy(userData.users ?? [], 'name'));

        try {
          const groupData = await droneApi.listGroups();
          setGroups(naturalSortBy(groupData.groups ?? [], 'displayName'));
        } catch {
          const grpSet = new Set();
          droneList.forEach(d => (d.groups ?? []).forEach(g => grpSet.add(g)));
          setGroups(
            [...grpSet]
              .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
              .map(g => ({ groupName: g, displayName: g, things: [] }))
          );
        }
      } catch (e) {
        console.error('初期データ取得エラー:', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (currentRange) fetchFlights(currentRange.from, currentRange.to);
  }, [currentRange, fetchFlights]);

  const droneMap = Object.fromEntries(drones.map(d => [d.thingName, d]));

  const calendarEvents = flights.map((f) => {
    let color;
    if (f.status === 'cancelled') {
      color = STATUS_COLORS.cancelled;
    } else if (f.status === 'completed') {
      color = STATUS_COLORS.completed;
    } else {
      color = FLIGHT_CATEGORIES[f.category]?.color ?? FLIGHT_CATEGORIES.flight.color;
    }
    return {
      id:              f.flightId,
      title:           f.displayName ?? f.thingName,
      start:           f.startTime,
      end:             f.endTime,
      backgroundColor: color,
      borderColor:     color,
      extendedProps:   f,
    };
  });

  const renderEventContent = (eventInfo) => {
    // mirror イベント（ドラッグ選択中のプレビュー）
    if (eventInfo.isMirror) {
      return (
        <div
          className="h-full"
          style={{
            background: 'rgba(0,90,200,0.22)',
            borderLeft: '2px solid rgba(162,201,255,0.5)',
          }}
        />
      );
    }

    const f = eventInfo.event.extendedProps;
    const categoryLabel = FLIGHT_CATEGORIES[f.category]?.label ?? '';
    const color = eventInfo.event.backgroundColor;
    return (
      <div
        className="h-full leading-tight overflow-hidden cursor-pointer"
        style={{ background: `${color}1a`, borderLeft: `2px solid ${color}`, padding: '2px 4px' }}
      >
        <div className="flex items-center gap-1 mb-0.5">
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
          <span style={{ fontSize: '8px', color, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.1em', fontWeight: 700 }}>
            {categoryLabel.toUpperCase()}
          </span>
        </div>
        <div className="font-black text-white truncate uppercase" style={{ fontSize: '10px', fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.05em' }}>
          {f.displayName ?? f.thingName}
        </div>
        {f.pilotName && (
          <div className="text-blue-300/60 truncate" style={{ fontSize: '9px', fontFamily: 'monospace' }}>
            {f.pilotName}
          </div>
        )}
      </div>
    );
  };

  const handleDatesSet = (info) => {
    const from = info.startStr.split('T')[0];
    const to   = info.endStr.split('T')[0];
    setCurrentRange({ from, to });
    fetchFlights(from, to);
  };

  const handleDateSelect = (info) => {
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    const start = info.allDay ? `${info.startStr}T09:00` : toLocal(info.start);
    const end   = info.allDay ? `${info.startStr}T10:00` : toLocal(info.end);
    setForm({ ...EMPTY_FORM, startTime: start, endTime: end });
    setSelectedFlight(null);
    setFormError('');
    setShowModal(true);
  };

  const handleEventClick = (info) => {
    setSelectedFlight(info.event.extendedProps);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'pilotId') {
        const user = users.find(u => u.userId === value);
        next.pilotName = user?.name ?? user?.email ?? '';
      }
      if (name === 'groupName') next.thingName = '';
      return next;
    });
  };

  const selectedGroupObj = groups.find(g => g.groupName === form.groupName);
  const formDrones       = form.groupName
    ? drones.filter(d =>
        (selectedGroupObj?.things ?? d.groups ?? []).includes(d.thingName) ||
        (d.groups ?? []).includes(form.groupName)
      )
    : drones;
  const availableDrones = formDrones.filter(d => d.operationalStatus === 'available');

  const handleSubmit = async () => {
    setFormError('');
    if (!form.pilotId)   return setFormError('パイロットを選択してください');
    if (!form.startTime) return setFormError('開始時刻を入力してください');
    if (!form.endTime)   return setFormError('終了時刻を入力してください');
    if (form.startTime >= form.endTime) return setFormError('終了時刻は開始時刻より後にしてください');

    const isGroupBulk = form.groupName && !form.thingName;
    if (isGroupBulk) {
      const targets = formDrones.filter(d => d.operationalStatus === 'available');
      if (targets.length === 0) return setFormError('グループ内に待機中の機体がありません');
      if (!window.confirm(`グループ「${form.groupName}」の待機中機体 ${targets.length} 台に一括予約します。よろしいですか？`)) return;
    } else if (!form.thingName) {
      return setFormError('機体を選択するか、グループの未選択で一括予約してください');
    }

    setSubmitting(true);
    try {
      if (isGroupBulk) {
        const targets = formDrones.filter(d => d.operationalStatus === 'available');
        const results = await Promise.allSettled(
          targets.map(d => flightApi.create({
            thingName: d.thingName,
            pilotId:   form.pilotId,
            pilotName: form.pilotName,
            title:     form.title,
            startTime: form.startTime,
            endTime:   form.endTime,
            location:  form.location,
            purpose:   form.purpose,
            notes:     form.notes,
            groupName: form.groupName,
            altitude:  form.altitude ? Number(form.altitude) : 0,
            category:  form.category,
          }))
        );
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          setFormError(`${targets.length - failed.length}件成功、${failed.length}件失敗（時間帯の重複等）`);
        } else {
          setShowModal(false);
        }
      } else {
        await flightApi.create({
          thingName: form.thingName,
          pilotId:   form.pilotId,
          pilotName: form.pilotName,
          title:     form.title,
          startTime: form.startTime,
          endTime:   form.endTime,
          location:  form.location,
          purpose:   form.purpose,
          notes:     form.notes,
          groupName: form.groupName,
          altitude:  form.altitude ? Number(form.altitude) : 0,
          category:  form.category,
        });
        setShowModal(false);
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      setFormError(
        e.message.includes('重複') || e.message.includes('CONFLICT') || e.message.includes('予約')
          ? 'この時間帯はすでに予約されています'
          : e.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (flight) => {
    if (!flight.groupName) {
      if (!window.confirm('この予約をキャンセルしますか？')) return;
      try {
        await flightApi.update(flight.flightId, { status: 'cancelled' });
        setShowModal(false);
        if (currentRange) fetchFlights(currentRange.from, currentRange.to);
      } catch (e) { alert(e.message); }
      return;
    }
    const choice = await showGroupConfirmFn(flight.groupName, 'キャンセル');
    if (!choice) return;
    try {
      if (choice === 'group') {
        const targets = flights.filter(f => f.groupName === flight.groupName && f.startTime === flight.startTime);
        await Promise.allSettled(targets.map(f => flightApi.update(f.flightId, { status: 'cancelled' })));
      } else {
        await flightApi.update(flight.flightId, { status: 'cancelled' });
      }
      setShowModal(false);
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async (flight) => {
    if (!flight.groupName) {
      if (!window.confirm('この予約を削除しますか？')) return;
      try {
        await flightApi.delete(flight.flightId);
        setShowModal(false);
        if (currentRange) fetchFlights(currentRange.from, currentRange.to);
      } catch (e) { alert(e.message); }
      return;
    }
    const choice = await showGroupConfirmFn(flight.groupName, '削除');
    if (!choice) return;
    try {
      if (choice === 'group') {
        const targets = flights.filter(f => f.groupName === flight.groupName && f.startTime === flight.startTime);
        await Promise.allSettled(targets.map(f => flightApi.delete(f.flightId)));
      } else {
        await flightApi.delete(flight.flightId);
      }
      setShowModal(false);
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) { alert(e.message); }
  };

  const showGroupConfirmFn = (groupName, action) => {
    return new Promise((resolve) => {
      setGroupConfirm({ groupName, action, resolve });
    });
  };

  const handleEventDrop = async (info) => {
    const flight = info.event.extendedProps;
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    const newStart      = toLocal(info.event.start);
    const oldStart      = flight.startTime.slice(0, 16);
    const diffMs        = new Date(newStart) - new Date(oldStart);
    const oldDurationMs = new Date(flight.endTime) - new Date(flight.startTime);
    const newEnd        = info.event.end
      ? toLocal(info.event.end)
      : toLocal(new Date(new Date(newStart).getTime() + oldDurationMs));

    try {
      if (flight.groupName) {
        const targets = flights.filter(f => f.groupName === flight.groupName && f.startTime.slice(0, 16) === oldStart);
        if (targets.length === 0) {
          await flightApi.update(flight.flightId, { startTime: newStart, endTime: newEnd });
        } else {
          await Promise.allSettled(targets.map(f => {
            const s   = toLocal(new Date(new Date(f.startTime).getTime() + diffMs));
            const dur = new Date(f.endTime) - new Date(f.startTime);
            const e   = toLocal(new Date(new Date(s).getTime() + dur));
            return flightApi.update(f.flightId, { startTime: s, endTime: e });
          }));
        }
      } else {
        await flightApi.update(flight.flightId, { startTime: newStart, endTime: newEnd });
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      info.revert();
      alert(e.message);
    }
  };

  const handleEventResize = async (info) => {
    const flight = info.event.extendedProps;
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    const newEnd = toLocal(info.event.end);
    try {
      if (flight.groupName) {
        const oldEnd  = flight.endTime.slice(0, 16);
        const diffMs  = new Date(newEnd) - new Date(oldEnd);
        const targets = flights.filter(f =>
          f.groupName === flight.groupName && f.startTime.slice(0, 16) === flight.startTime.slice(0, 16)
        );
        await Promise.allSettled(targets.map(f => {
          const e = toLocal(new Date(new Date(f.endTime).getTime() + diffMs));
          return flightApi.update(f.flightId, { endTime: e });
        }));
      } else {
        await flightApi.update(flight.flightId, { endTime: newEnd });
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      info.revert();
      alert(e.message);
    }
  };

  const handleUpdate = async (flight, updatedForm, choiceOverride) => {
    const payload = {
      pilotId:   updatedForm.pilotId,
      pilotName: updatedForm.pilotName,
      title:     updatedForm.title,
      startTime: updatedForm.startTime,
      endTime:   updatedForm.endTime,
      location:  updatedForm.location,
      purpose:   updatedForm.purpose,
      notes:     updatedForm.notes,
      altitude:  updatedForm.altitude ? Number(updatedForm.altitude) : 0,
      category:  updatedForm.category,
    };

    let choice = choiceOverride;
    if (flight.groupName && choice === null) {
      choice = await new Promise(resolve => {
        setTimeout(() => { setGroupConfirm({ groupName: flight.groupName, action: '変更', resolve }); }, 0);
      });
      if (!choice) return;
    }

    if (choice === 'single') {
      payload.groupName = '';
      await flightApi.update(flight.flightId, payload);
    } else if (choice === 'group') {
      const targets = flights.filter(f => f.groupName === flight.groupName && f.startTime === flight.startTime);
      await Promise.allSettled(targets.map(f => flightApi.update(f.flightId, payload)));
    } else {
      await flightApi.update(flight.flightId, payload);
    }
    setShowModal(false);
    if (currentRange) fetchFlights(currentRange.from, currentRange.to);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <HudSpinner label="INITIALIZING..." />
      </div>
    );
  }

  const modalTitle = selectedFlight
    ? `${(selectedFlight.displayName ?? selectedFlight.thingName).toUpperCase()} // ${STATUS_LABELS[selectedFlight.status] ?? selectedFlight.status}`
    : 'NEW_FLIGHT_PLAN';

  return (
    <>
    <style>{`
      .fc .fc-highlight {
        background: rgba(0,104,183,0.12) !important;
        border: none !important;
        border-left: 3px solid rgba(162,201,255,0.45) !important;
      }
      .fc-v-event.fc-mirror,
      .fc .fc-event.fc-mirror {
        background: rgba(0,104,183,0.18) !important;
        border: none !important;
        border-left: 3px solid rgba(162,201,255,0.55) !important;
        box-shadow: none !important;
        opacity: 1 !important;
      }
    `}</style>
    <div className="relative flex flex-col h-[calc(100vh-6.5rem)]">

      {/* ===== 背景右上 ぐるぐるアニメーション ===== */}
      <div className="absolute top-0 right-0 w-48 h-48 pointer-events-none opacity-20 z-0">
        <svg className="w-full h-full text-blue-500 animate-[spin_20s_linear_infinite]" viewBox="0 0 100 100">
          <circle cx="50" cy="50" fill="none" r="45" stroke="currentColor" strokeDasharray="10 5" strokeWidth="0.5" />
          <circle cx="50" cy="50" fill="none" r="35" stroke="currentColor" strokeDasharray="1 3" strokeWidth="1" />
          <path d="M 50 5 L 50 15 M 50 85 L 50 95 M 5 50 L 15 50 M 85 50 L 95 50" stroke="currentColor" strokeWidth="1" />
        </svg>
      </div>

      {/* ===== ページヘッダー ===== */}
      <div className="flex items-center justify-between border-b border-blue-900/30 pb-4 mt-6 mb-4 flex-shrink-0">
        <h2 className="font-label-caps text-xl font-bold tracking-[0.2em] italic text-blue-500 uppercase">
          機体予約・フライト管理
        </h2>
      </div>

      {/* ===== カレンダーエリア ===== */}
      <div className="flex-1 min-h-0 border border-blue-500/20 bg-slate-950/30 backdrop-blur-sm overflow-hidden">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView="timeGridWeek"
          locale={jaLocale}
          headerToolbar={{
            left:   'prev,next today',
            center: 'title',
            right:  'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
          }}
          buttonText={{ today: '今日', month: '月', week: '週', day: '日', list: 'リスト' }}
          height="100%"
          slotMinTime="06:00:00"
          slotMaxTime="21:00:00"
          slotDuration="00:30:00"
          events={calendarEvents}
          selectable={true}
          selectMirror={true}
          unselectAuto={false}
          select={handleDateSelect}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          editable={true}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventDisplay="block"
          eventContent={renderEventContent}
          nowIndicator={true}
        />
      </div>

      {/* ===== 凡例 ===== */}
      <div className="flex-shrink-0 flex flex-wrap gap-4 pt-3 mt-3 border-t border-blue-900/30">
        {Object.entries(FLIGHT_CATEGORIES).map(([key, { label, color }]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5" style={{ backgroundColor: color }} />
            <span className="font-label-caps text-[10px] text-on-surface-variant tracking-wider uppercase">{label}</span>
          </div>
        ))}
      </div>

      {/* ===== グループ確認モーダル ===== */}
      <HudModal
        isOpen={!!groupConfirm}
        onClose={() => { groupConfirm?.resolve(null); setGroupConfirm(null); }}
        title="OPERATION_TARGET"
        size="sm"
        footer={
          <>
            <HudButton variant="secondary" size="sm" onClick={() => { groupConfirm?.resolve(null); setGroupConfirm(null); }}>
              CANCEL
            </HudButton>
            <HudButton variant="secondary" size="sm" onClick={() => { groupConfirm?.resolve('single'); setGroupConfirm(null); }}>
              この機体のみ
            </HudButton>
            <HudButton variant="danger" size="sm" onClick={() => { groupConfirm?.resolve('group'); setGroupConfirm(null); }}>
              グループ全体
            </HudButton>
          </>
        }
      >
        {groupConfirm && (
          <div className="space-y-2 text-sm text-on-surface-variant">
            <p>「{groupConfirm.groupName}」グループで登録された予約です。</p>
            <p>{groupConfirm.action}する対象を選択してください。</p>
          </div>
        )}
      </HudModal>

      {/* ===== 予約モーダル ===== */}
      <HudModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        size="lg"
      >
        {selectedFlight ? (
          <FlightDetail
            flight={selectedFlight}
            droneMap={droneMap}
            users={users}
            onCancel={handleCancel}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
            onClose={() => setShowModal(false)}
          />
        ) : (
          <FlightForm
            form={form}
            groups={groups}
            availableDrones={availableDrones}
            allDrones={formDrones}
            users={users}
            error={formError}
            submitting={submitting}
            onChange={handleFormChange}
            onSubmit={handleSubmit}
            onClose={() => setShowModal(false)}
          />
        )}
      </HudModal>
    </div>
    </>
  );
}

// ---------- FlightDetail ----------

function FlightDetail({ flight, droneMap, users, onCancel, onDelete, onUpdate, onClose }) {
  const [form, setForm] = React.useState({
    pilotId:   flight.pilotId   ?? '',
    pilotName: flight.pilotName ?? '',
    title:     flight.title     ?? '',
    startTime: flight.startTime ? flight.startTime.slice(0, 16) : '',
    endTime:   flight.endTime   ? flight.endTime.slice(0, 16)   : '',
    location:  flight.location  ?? '',
    purpose:   flight.purpose   ?? '',
    notes:     flight.notes     ?? '',
    altitude:  flight.altitude  ?? '',
    category:  flight.category  ?? 'flight',
  });
  const [error,      setError]      = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const original = {
    pilotId:   flight.pilotId   ?? '',
    pilotName: flight.pilotName ?? '',
    title:     flight.title     ?? '',
    startTime: flight.startTime ? flight.startTime.slice(0, 16) : '',
    endTime:   flight.endTime   ? flight.endTime.slice(0, 16)   : '',
    location:  flight.location  ?? '',
    purpose:   flight.purpose   ?? '',
    notes:     flight.notes     ?? '',
    altitude:  flight.altitude  ?? '',
    category:  flight.category  ?? 'flight',
  };
  const isDirty = JSON.stringify(form) !== JSON.stringify(original);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'pilotId') {
        const user = users.find(u => u.userId === value);
        next.pilotName = user?.name ?? user?.email ?? '';
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.pilotId)   return setError('パイロットを選択してください');
    if (!form.startTime) return setError('開始時刻を入力してください');
    if (!form.endTime)   return setError('終了時刻を入力してください');
    if (form.startTime >= form.endTime) return setError('終了時刻は開始時刻より後にしてください');
    setSubmitting(true);
    try {
      await onUpdate(flight, form, flight.groupName ? null : 'single');
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const statusVariant =
    flight.status === 'planned'   ? 'primary' :
    flight.status === 'in_flight' ? 'success' :
    flight.status === 'cancelled' ? 'danger'  : 'default';

  return (
    <div className="space-y-4">
      <HudAlert message={error} />

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <HudInput label="タイトル（任意）" name="title" value={form.title} onChange={handleChange} placeholder="例: 現場点検フライト" />
        </div>

        <HudSelect
          label="パイロット"
          name="pilotId"
          value={form.pilotId}
          onChange={handleChange}
          options={[
            { value: '', label: '選択してください' },
            ...users.map(u => ({ value: u.userId, label: u.name ?? u.email })),
          ]}
        />

        <div>
          <label className="block font-label-caps text-xs tracking-[0.15em] text-slate-500 mb-1 uppercase">ステータス</label>
          <div className="flex items-center gap-2 pt-1">
            <HudBadge variant={statusVariant}>{STATUS_LABELS[flight.status] ?? flight.status}</HudBadge>
            {flight.groupName && <HudBadge variant="default">{flight.groupName}</HudBadge>}
          </div>
        </div>

        <HudSelect
          label="予約種別"
          name="category"
          value={form.category}
          onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
          options={Object.entries(FLIGHT_CATEGORIES).map(([key, { label }]) => ({ value: key, label }))}
        />

        <HudInput label="開始日時" type="datetime-local" name="startTime" value={form.startTime} onChange={handleChange} />
        <HudInput label="終了日時" type="datetime-local" name="endTime"   value={form.endTime}   onChange={handleChange} />
        <HudInput label="場所"     name="location" value={form.location} onChange={handleChange} placeholder="例: 東京都港区海岸" />
        <HudInput label="最大高度 (m)" type="number" name="altitude" value={form.altitude} onChange={handleChange} placeholder="例: 150" />

        <div className="col-span-2">
          <HudInput label="目的" name="purpose" value={form.purpose} onChange={handleChange} placeholder="例: 測量・点検・農薬散布" />
        </div>
        <div className="col-span-2">
          <HudTextarea label="備考" name="notes" value={form.notes} onChange={handleChange} rows={3} />
        </div>
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-blue-900/40">
        <div className="flex gap-2">
          {flight.status === 'planned' && (
            <HudButton variant="danger" size="sm" onClick={() => onCancel(flight)} disabled={submitting}>
              キャンセル
            </HudButton>
          )}
          {['planned', 'cancelled'].includes(flight.status) && (
            <HudButton variant="danger" size="sm" icon="delete" onClick={() => onDelete(flight)} disabled={submitting}>
              削除
            </HudButton>
          )}
        </div>
        <div className="flex gap-2">
          <HudButton variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            CLOSE
          </HudButton>
          {isDirty && (
            <HudButton size="sm" onClick={handleSave} loading={submitting}>
              変更を保存
            </HudButton>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- FlightForm ----------

function FlightForm({ form, groups, availableDrones, allDrones, users, error, submitting, onChange, onSubmit, onClose }) {
  const [activeTab, setActiveTab] = React.useState('basic');

  return (
    <div className="space-y-4">
      <HudAlert message={error} />

      {/* タブ見出し */}
      <div className="flex border-b border-blue-900/40">
        {[
          { key: 'basic',  label: 'BASIC'  },
          { key: 'detail', label: 'DETAIL' },
        ].map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`
              px-4 py-2 font-label-caps text-xs tracking-[0.15em] transition-colors
              ${activeTab === key
                ? 'border-b-2 border-primary text-primary -mb-px'
                : 'text-slate-500 hover:text-white border-b-2 border-transparent -mb-px'}
            `}
          >
            {label}
          </button>
        ))}
      </div>

      {/* タブコンテンツ — min-h で両タブ共通の高さを確保しサイズ変化を防ぐ */}
      <div className="min-h-[22rem]">

        {/* BASIC タブ */}
        {activeTab === 'basic' && (
          <div className="space-y-4">
            {groups.length > 0 && (
              <HudSelect
                label="グループ（一括予約用）"
                name="groupName"
                value={form.groupName}
                onChange={onChange}
                options={[
                  { value: '', label: '指定なし' },
                  ...groups.map(g => ({ value: g.groupName, label: g.displayName })),
                ]}
              />
            )}

            <HudSelect
              label="機体"
              name="thingName"
              value={form.thingName}
              onChange={onChange}
              options={[
                { value: '', label: '選択してください' },
                ...(availableDrones.length > 0
                  ? [
                      { value: '', label: '── 待機中 ──', disabled: true },
                      ...availableDrones.map(d => ({ value: d.thingName, label: d.displayName })),
                      ...(allDrones.filter(d => d.operationalStatus !== 'available').length > 0
                        ? [
                            { value: '', label: '── 点検中・修理中 ──', disabled: true },
                            ...allDrones.filter(d => d.operationalStatus !== 'available').map(d => ({
                              value:    d.thingName,
                              label:    `${d.displayName}（${d.operationalStatus === 'maintenance' ? '点検中' : '修理中'}）`,
                              disabled: true,
                            })),
                          ]
                        : [])
                    ]
                  : allDrones.map(d => ({ value: d.thingName, label: d.displayName }))),
              ]}
            />

            <div className="grid grid-cols-2 gap-3">
              <HudInput label="開始日時" type="datetime-local" name="startTime" value={form.startTime} onChange={onChange} />
              <HudInput label="終了日時" type="datetime-local" name="endTime"   value={form.endTime}   onChange={onChange} />
            </div>

            <HudSelect
              label="パイロット"
              name="pilotId"
              value={form.pilotId}
              onChange={onChange}
              options={[
                { value: '', label: '選択してください' },
                ...users.map(u => ({ value: u.userId, label: u.name ?? u.email })),
              ]}
            />

            <HudSelect
              label="予約種別"
              name="category"
              value={form.category}
              onChange={onChange}
              options={Object.entries(FLIGHT_CATEGORIES).map(([key, { label }]) => ({ value: key, label }))}
            />
          </div>
        )}

        {/* DETAIL タブ */}
        {activeTab === 'detail' && (
          <div className="space-y-4">
            <HudInput label="タイトル" name="title" value={form.title} onChange={onChange} placeholder="例: 現場点検フライト" />
            <div className="grid grid-cols-2 gap-3">
              <HudInput label="場所"         name="location" value={form.location} onChange={onChange} placeholder="例: 東京都港区海岸" />
              <HudInput label="最大高度 (m)" type="number" name="altitude" value={form.altitude} onChange={onChange} placeholder="例: 150" />
            </div>
            <HudInput label="目的" name="purpose" value={form.purpose} onChange={onChange} placeholder="例: 測量・点検・農薬散布" />
            <HudTextarea label="備考" name="notes" value={form.notes} onChange={onChange} rows={3} placeholder="特記事項があれば入力" />
          </div>
        )}

      </div>

      <div className="flex justify-end gap-3 pt-3 border-t border-blue-900/40">
        <HudButton variant="secondary" onClick={onClose}>
          CANCEL
        </HudButton>
        <HudButton onClick={onSubmit} loading={submitting}>
          REGISTER_FLIGHT
        </HudButton>
      </div>
    </div>
  );
}
